/** Claim fornitori: il file dell'amministrazione dentro il portale. KPI, elenco con blocco pagamento, nuovo claim. */
import { App, Button, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select, Space, Table } from "antd";
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { STATI_CLAIM, VALUTE, type ClaimDto, type ClaimElencoDto, type ClaimRigaDto, type NuovoClaimInput } from "@qa/shared";
import { api, download } from "../../api/client";
import { fmtNum, numFormatter, numParser } from "../../lib/format";
import { usePuo } from "../../lib/permessi";
import { useAggiorna } from "../../lib/query";
import { messaggioErrore } from "../../i18n/errori";
import { FornitoreSelect } from "../../components/campi";
import { useStretto } from "../../lib/schermo";
import { Coppia, ElencoSchede } from "../../components/Elenco";
import { Kpi, KpiRow, Miss, PageHead, Prop, StatoClaimTag } from "../../components/ui";

export function BloccoSelect({ c, disabled }: { c: Pick<ClaimRigaDto, "id" | "bloccoPagamento">; disabled?: boolean }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const m = useMutation({
    mutationFn: (v: boolean | null) => api.patch<ClaimDto>(`/claim/${c.id}`, { bloccoPagamento: v }),
    onSuccess: () => void aggiorna(),
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const v = c.bloccoPagamento === null ? "nd" : c.bloccoPagamento ? "si" : "no";
  return (
    <Select
      size="small"
      value={v}
      disabled={disabled || m.isPending}
      onChange={(x) => m.mutate(x === "nd" ? null : x === "si")}
      style={{ width: 150 }}
      options={[
        { value: "nd", label: t("comune.trattino") },
        { value: "si", label: t("claim.scheda.bloccoSi") },
        { value: "no", label: t("azioni.no") },
      ]}
    />
  );
}

function NuovoClaim({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const nav = useNavigate();
  const aggiorna = useAggiorna();
  const [form] = Form.useForm<NuovoClaimInput>();
  const m = useMutation({
    mutationFn: (v: NuovoClaimInput) => api.post<ClaimDto>("/claim", { ...v, brand: v.brand || null, risoluzione: v.risoluzione || null, importo: v.importo ?? null }),
    onSuccess: (c) => {
      message.success(t("claim.nuovo.creato", { codice: c.codice }));
      void aggiorna();
      nav(`/claim/${c.id}`);
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  const obbl = [{ required: true, whitespace: true, message: t("redflag.nuova.obbligatorio") }];
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t("claim.nuovo.titolo")}
      okText={t("azioni.salva")}
      cancelText={t("azioni.annulla")}
      confirmLoading={m.isPending}
      onOk={() => form.submit()}
      destroyOnHidden
    >
      <Form<NuovoClaimInput> form={form} layout="vertical" initialValues={{ valuta: "EUR" }} onFinish={(v) => m.mutate(v)}>
        <Form.Item name="fornitoreId" label={t("claim.nuovo.fornitore")}>
          <FornitoreSelect
            onChange={(id, f) => {
              form.setFieldValue("fornitoreId", id);
              if (f && !form.getFieldValue("fornitoreTesto")) form.setFieldValue("fornitoreTesto", f.nome);
            }}
          />
        </Form.Item>
        <Form.Item name="fornitoreTesto" label={t("claim.nuovo.fornitoreTesto")} rules={obbl}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="brand" label={t("claim.nuovo.brand")}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="problema" label={t("claim.nuovo.problema")} rules={obbl}>
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
        <Form.Item name="risoluzione" label={t("claim.nuovo.risoluzione")}>
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item name="importo" label={t("claim.nuovo.importo")}>
              <InputNumber min={0} style={{ width: "100%" }} formatter={numFormatter} parser={numParser} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="valuta" label={t("claim.nuovo.valuta")}>
              <Select options={VALUTE.map((v) => ({ value: v, label: v }))} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default function ClaimPage() {
  const { t } = useTranslation();
  const puo = usePuo();
  const stretto = useStretto();
  const [sp, setSp] = useSearchParams();
  const [nuovo, setNuovo] = useState(false);
  const f = { q: sp.get("q") ?? undefined, stato: sp.get("stato") ?? undefined, aperti: sp.get("aperti") === "1" ? true : undefined, fornitoreId: sp.get("fornitoreId") ?? undefined };
  const set = (k: string, v: string | undefined) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n, { replace: true });
  };
  const q = useQuery({ queryKey: ["claim-elenco", f], queryFn: () => api.get<ClaimElencoDto>("/claim", { ...f, limit: 500 }), placeholderData: keepPreviousData });
  const k = q.data?.kpi;
  const gestisce = puo("claim.gestisci");
  return (
    <>
      <PageHead
        crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoClaim") }]}
        title={t("claim.elenco.titolo")}
        sub={t("claim.elenco.sottotitolo")}
        extra={
          <>
            <Button icon={<DownloadOutlined />} onClick={() => download("/claim/export.xlsx", f)}>
              {t("azioni.esportaXlsx")}
            </Button>
            {gestisce && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setNuovo(true)}>
                {t("claim.elenco.nuovo")}
              </Button>
            )}
          </>
        }
      />
      <KpiRow>
        <Kpi label={t("claim.elenco.kpiAperti")} value={fmtNum(k?.aperti)} />
        <Kpi sensibile label={t("claim.elenco.kpiUsd")} value={k ? `${fmtNum(k.valoreUsd)} $` : "—"} />
        <Kpi sensibile label={t("claim.elenco.kpiEur")} value={k ? `${fmtNum(k.valoreEur)} €` : "—"} />
        <Kpi label={t("claim.elenco.kpiCollegati")} value={fmtNum(k?.collegati)} note={k?.collegamentiProposti ? t("claim.elenco.kpiProposti", { n: k.collegamentiProposti }) : undefined} />
        <Kpi label={t("claim.elenco.kpiBloccati")} value={fmtNum(k?.pagamentiBloccati)} />
      </KpiRow>
      <Space wrap style={{ margin: "16px 0 14px" }}>
        <Input.Search
          allowClear
          defaultValue={f.q}
          placeholder={t("claim.elenco.cerca")}
          aria-label={t("claim.elenco.cerca")}
          onSearch={(v) => set("q", v.trim() || undefined)}
          style={{ width: stretto ? "100%" : 260 }}
        />
        <Select
          allowClear
          placeholder={t("claim.elenco.tuttiStati")}
          value={f.stato}
          onChange={(v) => set("stato", v)}
          style={{ width: stretto ? "calc(50% - 4px)" : 200 }}
          options={STATI_CLAIM.map((s) => ({ value: s, label: t(`dominio.statoClaim.${s}`) }))}
        />
        <Checkbox checked={!!f.aperti} onChange={(e) => set("aperti", e.target.checked ? "1" : undefined)}>
          {t("claim.elenco.soloAperti")}
        </Checkbox>
      </Space>
      {stretto ? (
        <ElencoSchede
          righe={q.data?.righe ?? []}
          chiave={(c) => c.id}
          loading={q.isFetching}
          vuoto={q.isError ? messaggioErrore(q.error) : t("claim.elenco.vuoto")}
          scheda={(c) => (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <Link className="mono" to={`/claim/${c.id}`}>
                  {c.codice}
                </Link>
                <StatoClaimTag s={c.stato} />
              </div>
              <div style={{ marginTop: 4 }}>
                <b>{c.fornitoreTesto}</b>
                {c.brand && <span className="muted"> · {c.brand}</span>}
              </div>
              {c.problema && <div className="small">{c.problema}</div>}
              <Coppia l={c.valuta}>
                <span className="num" data-sensitive>{fmtNum(c.importo)}</span>
              </Coppia>
              <Coppia l={t("claim.elenco.redflag")}>
                {c.segnalazione ? (
                  <Link className="mono" to={`/redflag/segnalazioni/${c.segnalazione.id}`}>
                    {c.segnalazione.codice}
                  </Link>
                ) : c.segnalazioneProposta ? (
                  <Prop>{c.segnalazioneProposta.codice}</Prop>
                ) : (
                  "—"
                )}
              </Coppia>
              <Coppia l={t("claim.elenco.blocco")}>
                {gestisce ? <BloccoSelect c={c} /> : c.bloccoPagamento === null ? <Miss>{t("comune.nonIndicato")}</Miss> : c.bloccoPagamento ? t("azioni.si") : t("azioni.no")}
              </Coppia>
            </>
          )}
        />
      ) : (
        <Table<ClaimRigaDto>
          rowKey="id"
          size="middle"
          loading={q.isFetching}
          dataSource={q.data?.righe ?? []}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          scroll={{ x: 1200 }}
          locale={{ emptyText: q.isError ? messaggioErrore(q.error) : t("claim.elenco.vuoto") }}
          columns={[
            {
              title: t("claim.elenco.claim"),
              key: "c",
              width: 80,
              render: (_, c) => (
                <Link className="mono" to={`/claim/${c.id}`}>
                  {c.codice}
                </Link>
              ),
            },
            {
              title: t("claim.elenco.fornitore"),
              key: "f",
              render: (_, c) => (c.fornitore && puo("fornitore.vedi") ? <Link to={`/fornitori/${c.fornitore.id}`}>{c.fornitoreTesto || c.fornitore.nome}</Link> : c.fornitoreTesto),
            },
            { title: t("claim.elenco.brand"), dataIndex: "brand" },
            { title: t("claim.elenco.problema"), dataIndex: "problema" },
            { title: t("claim.elenco.risoluzione"), dataIndex: "risoluzione" },
            { title: t("claim.elenco.usd"), key: "usd", align: "right", render: (_, c) => <span className="num" data-sensitive>{c.valuta === "USD" ? fmtNum(c.importo) : ""}</span> },
            { title: t("claim.elenco.eur"), key: "eur", align: "right", render: (_, c) => <span className="num" data-sensitive>{c.valuta === "EUR" ? fmtNum(c.importo) : ""}</span> },
            {
              title: t("claim.elenco.redflag"),
              key: "rf",
              render: (_, c) =>
                c.segnalazione ? (
                  <Link className="mono" to={`/redflag/segnalazioni/${c.segnalazione.id}`}>
                    {c.segnalazione.codice}
                  </Link>
                ) : c.segnalazioneProposta ? (
                  <Prop>
                    {c.segnalazioneProposta.codice} · {c.segnalazioneProposta.daVerificare ? t("redflag.scheda.claimDaVerificare") : t("comune.proposto")}
                  </Prop>
                ) : (
                  "—"
                ),
            },
            { title: t("claim.elenco.stato"), key: "s", render: (_, c) => <StatoClaimTag s={c.stato} /> },
            {
              title: t("claim.elenco.blocco"),
              key: "b",
              width: 170,
              render: (_, c) => (gestisce ? <BloccoSelect c={c} /> : c.bloccoPagamento === null ? <Miss>{t("comune.nonIndicato")}</Miss> : c.bloccoPagamento ? t("azioni.si") : t("azioni.no")),
            },
          ]}
        />
      )}
      <p className="muted small">{t("claim.elenco.nota")}</p>
      {nuovo && <NuovoClaim open={nuovo} onClose={() => setNuovo(false)} />}
    </>
  );
}
